import type { McpResponseModel } from '@/customApiClients/mgmtApiClient/Api';
import { Search } from 'lucide-react';
import { useMemo, useState } from 'react';

import { DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';

import {
    SOURCE_CATEGORY_TABS,
    type SourceCategoryTabId,
    getSourceCategory
} from '@/types/sourceTypeRegistry';

import SourceCard from './DMSourceCard';

export interface ResourceSelectionPanelProps {
    sources: McpResponseModel[];
    selectedSources: McpResponseModel[];
    isLoading?: boolean;
    error?: string | null;
    disabled?: boolean;
    onToggleSource: (source: McpResponseModel) => void;
    onClose?: () => void;
    title?: string;
    description?: string;
}

export default function ResourceSelectionPanel({
    sources,
    selectedSources,
    isLoading = false,
    error = null,
    disabled = false,
    onToggleSource,
    title = 'Organizational Sources',
    description = 'Allow your chat agent to access information from selected sources.'
}: ResourceSelectionPanelProps) {
    const [activeTab, setActiveTab] = useState<SourceCategoryTabId>('all');
    const [searchQuery, setSearchQuery] = useState('');

    // Filter sources by tab category and search query
    const filteredSources = useMemo(() => {
        return sources.filter((source) => {
            // Filter by tab
            if (activeTab !== 'all') {
                const category = getSourceCategory(source.configType);
                if (category !== activeTab) {
                    return false;
                }
            }

            // Filter by search
            if (searchQuery.trim()) {
                const query = searchQuery.toLowerCase();
                const name = (
                    source.displayName ||
                    source.name ||
                    ''
                ).toLowerCase();
                const desc = (source.description || '').toLowerCase();
                return name.includes(query) || desc.includes(query);
            }

            return true;
        });
    }, [sources, activeTab, searchQuery]);

    // Check if a source is selected
    const isSourceSelected = (source: McpResponseModel) =>
        selectedSources.some((s) => s.name === source.name);

    // Count sources per category for tab badges (optional)
    const categoryCounts = useMemo(() => {
        const counts: Record<SourceCategoryTabId, number> = {
            all: sources.length,
            reports: 0,
            databases: 0,
            files: 0,
            apis: 0,
            documents: 0
        };

        sources.forEach((source) => {
            const category = getSourceCategory(source.configType);
            if (category !== 'all') {
                counts[category]++;
            }
        });

        return counts;
    }, [sources]);

    return (
        <div className="flex flex-col gap-8 bg-background shadow-md p-10 rounded-lg w-full h-full">
            {/* Header Section */}
            <div className="flex justify-between items-start">
                <div className="flex flex-col gap-6 max-w-[540px]">
                    {/* Title & Description */}
                    <div className="flex flex-col gap-1">
                        <DialogTitle className="font-medium text-foreground leading-8">
                            {title}
                        </DialogTitle>
                        <p className="text-foreground-secondary text-base leading-6">
                            {description}
                        </p>
                    </div>

                    {/* Search Input */}
                    <div className="relative w-[320px]">
                        <Search className="top-1/2 left-3 absolute w-5 h-5 text-muted-foreground -translate-y-1/2" />
                        <Input
                            type="text"
                            placeholder="Search Sources..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-10 h-10"
                        />
                    </div>
                </div>
            </div>

            {/* Tab Navigation */}
            <Tabs
                value={activeTab}
                onValueChange={(value) =>
                    setActiveTab(value as SourceCategoryTabId)
                }
            >
                <TabsList className="flex justify-start bg-background p-0 border-b rounded-none w-full">
                    {SOURCE_CATEGORY_TABS.map((tab) => {
                        const count = categoryCounts[tab.id];
                        // Hide tabs with no items (except 'all')
                        if (tab.id !== 'all' && count === 0) return null;

                        return (
                            <TabsTrigger
                                key={tab.id}
                                value={tab.id}
                                className="bg-background data-[state=active]:shadow-none border-0 border-transparent data-[state=active]:border-foreground hover:border-muted-foreground/30 dark:data-[state=active]:border-foreground border-b-2 rounded-none h-full text-muted-foreground data-[state=active]:text-foreground hover:text-foreground dark:hover:text-foreground dark:text-muted-foreground text-sm"
                            >
                                {tab.label}
                            </TabsTrigger>
                        );
                    })}
                </TabsList>
            </Tabs>

            {/* Sources Grid */}
            <div className="flex-1 overflow-y-auto">
                {/* Loading state */}
                {isLoading && (
                    <div className="flex justify-center items-center py-8">
                        <p className="text-muted-foreground text-sm">
                            Loading sources…
                        </p>
                    </div>
                )}

                {/* Error state */}
                {error && !isLoading && (
                    <div className="flex justify-center items-center py-8">
                        <p className="text-destructive text-sm">{error}</p>
                    </div>
                )}

                {/* Empty state */}
                {!isLoading && !error && filteredSources.length === 0 && (
                    <div className="flex justify-center items-center py-8">
                        <p className="text-muted-foreground text-sm">
                            {searchQuery
                                ? 'No sources match your search'
                                : 'No sources available'}
                        </p>
                    </div>
                )}

                {/* Sources grid */}
                {!isLoading && !error && filteredSources.length > 0 && (
                    <div className="gap-4 grid grid-cols-2">
                        {filteredSources.map((source) => (
                            <SourceCard
                                key={source.name}
                                source={source}
                                isSelected={isSourceSelected(source)}
                                disabled={disabled}
                                onToggle={onToggleSource}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
